import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import { Flex, Avatar, IconButton, Empty, Tag, Popconfirm, Text, Icon } from '@nula/core-ui';
import { useUIStore } from '@/stores/use-ui-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useSyncSystem } from '@/lib/sync/sync-system-context';
import { listThreads, setResolved, deleteThread, type CommentThread } from '@/lib/comments/comment-threads';
import { SidePanel } from '@/components/side-panel/side-panel';
import styles from './comment-sidebar.module.css';

export interface CommentSidebarProps {
  entityId: string;
}

function relativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const rtf = new Intl.RelativeTimeFormat(navigator.language, { numeric: 'auto' });
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  if (diffSec < 3600) return rtf.format(-Math.floor(diffSec / 60), 'minute');
  if (diffSec < 86400) return rtf.format(-Math.floor(diffSec / 3600), 'hour');
  return rtf.format(-Math.floor(diffSec / 86400), 'day');
}

export function CommentSidebar({ entityId }: CommentSidebarProps) {
  const { registry } = useSyncSystem();
  const setCommentsPanelOpen = useUIStore((s) => s.setCommentsPanelOpen);
  const authUser = useAuthStore((s) => s.user);
  const currentUserId = authUser?.sub;

  const [threads, setThreads] = useState<CommentThread[]>([]);

  useEffect(() => {
    const doc = registry.getDoc(entityId);
    if (!doc) return;

    const map = doc.getMap('editor-comment-threads');
    const refresh = () => setThreads(listThreads(doc));
    refresh();
    map.observeDeep(refresh);
    return () => map.unobserveDeep(refresh);
  }, [registry, entityId]);

  const handleDelete = (doc: Y.Doc, threadId: string) => {
    deleteThread(doc, threadId);
  };

  const handleToggleResolved = (doc: Y.Doc, thread: CommentThread) => {
    setResolved(doc, thread.id, !thread.resolved);
  };

  const getDoc = () => registry.getDoc(entityId);

  return (
    <SidePanel
      title="Comments"
      icon="comment"
      titleExtra={<Tag color="default">{threads.length}</Tag>}
      onClose={() => setCommentsPanelOpen(false)}
    >
      <Flex vertical gap="md" className={styles.commentList}>
        {threads.length === 0 ? (
          <Empty
            image={<Icon name="chat_bubble" size={48} className={styles.emptyIcon} />}
            description="No comments yet"
            className={styles.emptyState}
          />
        ) : (
          threads.map((thread) => {
            const doc = getDoc();
            return (
              <Flex
                key={thread.id}
                vertical
                gap="xs"
                className={styles.thread}
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent('nula-editor-focus-comment', { detail: { threadId: thread.id } }),
                  )
                }
              >
                <Flex align="center" justify="space-between" className={styles.threadHeader}>
                  {thread.resolved && (
                    <Tag color="success" className={styles.resolvedTag}>Resolved</Tag>
                  )}
                  <Flex gap="xs" className={styles.threadActions}>
                    {doc && (
                      <>
                        <IconButton
                          size="sm"
                          transparent
                          aria-label={thread.resolved ? 'Reopen' : 'Resolve'}
                          onClick={() => handleToggleResolved(doc, thread)}
                        >
                          <Icon name={thread.resolved ? 'replay' : 'check_circle'} size={14} />
                        </IconButton>
                        {currentUserId && (
                          <Popconfirm
                            title="Delete this thread?"
                            placement="bottom-start"
                            onConfirm={() => handleDelete(doc, thread.id)}
                          >
                            <IconButton size="sm" transparent aria-label="Delete thread">
                              <Icon name="delete" size={14} />
                            </IconButton>
                          </Popconfirm>
                        )}
                      </>
                    )}
                  </Flex>
                </Flex>

                {thread.comments.map((c) => (
                  <Flex key={c.id} gap="xs">
                    <Avatar size="sm" className={styles.avatar}>
                      {c.author.charAt(0).toUpperCase()}
                    </Avatar>
                    <Flex vertical className={styles.commentBody}>
                      <Flex align="center" gap="xs" className={styles.commentHeader}>
                        <Text strong className={styles.authorName}>{c.author}</Text>
                        <Text className={styles.timestamp}>{relativeTime(c.createdAt)}</Text>
                      </Flex>
                      <Text className={styles.commentText}>{c.text}</Text>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            );
          })
        )}
      </Flex>
    </SidePanel>
  );
}
